import React, { useState, useEffect } from "react";
import {
  ShoppingBag,
  Menu,
  X,
  ArrowRight,
  Instagram,
  Search,
  Heart,
} from "lucide-react";

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const articles = [
    {
      id: 1,
      tag: "EDITORIAL",
      title: "The Silence of Linen",
      desc: "여름의 끝자락, 우리가 선택한 가장 정적인 소재에 대하여.",
      img: "https://images.unsplash.com/photo-1509631179647-0177331693ae?auto=format&fit=crop&q=80&w=800",
    },
    {
      id: 2,
      tag: "ISSUE NO.04",
      title: "What are you wearing today?",
      desc: "도심 속 5인의 크리에이터가 답하는 오늘의 착장과 철학.",
      img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&q=80&w=800",
    },
  ];

  const products = [
    {
      id: 1,
      name: "Canvas Oversized Shirt",
      price: "₩128,000",
      img: "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 2,
      name: "Raw Denim '01",
      price: "₩189,000",
      img: "https://images.unsplash.com/photo-1542272604-787c3835535d?auto=format&fit=crop&q=80&w=600",
    },
    {
      id: 3,
      name: "Cashmere Turtleneck",
      price: "₩245,000",
      img: "https://www.mytheresa.com/media/1094/1238/100/71/P01045345_d1.jpg",
    },
    {
      id: 4,
      name: "Structured Wool Coat",
      price: "₩420,000",
      img: "https://thumbnail.coupangcdn.com/thumbnails/remote/492x492ex/image/vendor_inventory/09b4/4a9332de39ae9afcadecb6bff063b6415fc3c6425784e3306a07950f486b.jpg",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1a1a1a] font-serif overflow-x-hidden">
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 px-6 py-4 flex justify-between items-center ${
          scrolled
            ? "bg-white/90 backdrop-blur-md border-b border-gray-200 py-3"
            : ""
        }`}
      >
        <div className="flex items-center space-x-6">
          <button
            onClick={() => setIsMenuOpen(true)}
            className="hover:rotate-90 transition-transform"
          >
            <Menu size={24} />
          </button>
          <Search size={20} className="hidden md:block cursor-pointer" />
        </div>

        {/* Logo Section */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
          <a href="/" className="block">
            {/* 로고 이미지가 여기에 들어갑니다. src 부분에 실제 로고 주소를 넣어주세요. */}
            <img
              src="/mylogo.png"
              alt="Brand Logo"
              className={`h-12 md:h-14 transition-all duration-500 ${
                scrolled ? "scale-90" : "scale-100"
              }`}
              style={{
                filter: scrolled ? "invert(0)" : "invert(1)",
              }} /* 배경색에 맞춰 색상 반전 처리 예시 */
            />
            <span className="sr-only">Brand Name</span>
          </a>
        </div>

        <div className="flex items-center space-x-6">
          <Heart size={20} className="hidden md:block cursor-pointer" />
          <div className="flex items-center space-x-2 cursor-pointer group">
            <ShoppingBag size={24} />
            <span className="text-sm font-sans font-bold group-hover:underline">
              BAG(0)
            </span>
          </div>
        </div>
      </nav>

      {/* Fullscreen Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-[#1a1a1a] text-white p-10 flex flex-col justify-between animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex justify-between items-start">
            <span className="text-xs font-sans tracking-[0.3em] opacity-50 uppercase">
              The Navigation
            </span>
            <button onClick={() => setIsMenuOpen(false)}>
              <X size={32} />
            </button>
          </div>
          <div className="flex flex-col space-y-4">
            {["EDITORIAL", "SHOP", "ARCHIVE", "ABOUT", "CONTACT"].map(
              (item) => (
                <a
                  key={item}
                  href="#"
                  className="text-5xl md:text-8xl font-black hover:italic hover:pl-8 transition-all duration-300"
                >
                  {item}
                </a>
              )
            )}
          </div>
          <div className="flex justify-between items-end font-sans text-sm border-t border-white/20 pt-8">
            <div className="flex space-x-8">
              <span>INSTAGRAM</span>
              <span>PINTEREST</span>
            </div>
            <span>© 2024 ALL RIGHTS RESERVED.</span>
          </div>
        </div>
      )}

      {/* Hero Section / Magazine Cover */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&q=80&w=2000"
            alt="Hero Cover"
            className="w-full h-full object-cover grayscale-[20%] scale-105"
          />
          <div className="absolute inset-0 bg-black/10"></div>
        </div>

        <div className="relative z-10 text-center text-white px-4">
          <p className="text-xs font-sans tracking-[0.5em] mb-4 uppercase drop-shadow-lg">
            Special Editorial Issue
          </p>
          <h2 className="text-5xl md:text-[10rem] font-black leading-none uppercase mb-8 drop-shadow-2xl">
            Today.
          </h2>
          <div className="max-w-md mx-auto">
            <p className="text-lg md:text-2xl font-light italic mb-8 opacity-90 leading-relaxed">
              "What are you weareing today?"
            </p>
            <button className="px-8 py-3 border border-white hover:bg-white hover:text-black transition-colors duration-500 font-sans text-xs tracking-widest uppercase">
              Enter The Shop
            </button>
          </div>
        </div>

        {/* Floating Sidebar info */}
        <div className="absolute bottom-10 left-10 hidden md:block text-white font-sans text-[10px] tracking-widest leading-loose">
          VOL. 01 / ISSUE 04
          <br />
          SEOUL, KR
          <br />
          2024. 03. 12
        </div>
      </section>

      {/* Editorial Grid */}
      <section className="py-24 px-6 md:px-20">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {articles.map((article) => (
            <div key={article.id} className="group cursor-pointer">
              <div className="overflow-hidden mb-6 aspect-[4/5]">
                <img
                  src={article.img}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <span className="text-[10px] font-sans font-bold tracking-[0.2em] border-b border-black pb-1 uppercase">
                {article.tag}
              </span>
              <h3 className="text-3xl mt-4 mb-2">{article.title}</h3>
              <p className="text-gray-600 font-sans text-sm leading-relaxed">
                {article.desc}
              </p>
              <button className="mt-4 flex items-center text-xs font-bold font-sans tracking-widest group-hover:gap-4 transition-all uppercase">
                Read Story <ArrowRight size={14} className="ml-2" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Large Typography Interruption */}
      <section className="bg-white py-40 flex flex-col items-center justify-center overflow-hidden">
        <div className="whitespace-nowrap flex animate-marquee border-y border-black/10 py-8">
          {[1, 2, 3].map((i) => (
            <h2
              key={i}
              className="text-7xl md:text-9xl font-black uppercase tracking-tighter mx-4 opacity-10"
            >
              What are you weareing today? —
            </h2>
          ))}
        </div>
        <div className="max-w-2xl px-6 text-center mt-20">
          <p className="text-2xl md:text-4xl leading-snug italic">
            "당신의 하루를 입다, WEAREING"
          </p>
          <p className="mt-6 text-xs font-sans tracking-widest text-gray-400">
            — 위어링 EDITOR'S NOTE
          </p>
        </div>
      </section>

      {/* Shop / Archive Grid */}
      <section className="py-24 px-6 md:px-20 bg-white">
        <div className="flex justify-between items-end mb-16">
          <div>
            <span className="text-xs font-sans tracking-widest uppercase opacity-40">
              The Archive
            </span>
            <h2 className="text-4xl mt-2">Essential Pieces</h2>
          </div>
          <button className="hidden md:block text-xs font-bold font-sans border-b border-black pb-1 uppercase">
            Shop All Collection
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {products.map((product) => (
            <div key={product.id} className="group">
              <div className="relative overflow-hidden aspect-[3/4] mb-4 bg-[#f0f0f0]">
                <img
                  src={product.img}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                />
                <button className="absolute bottom-4 left-4 right-4 bg-white py-3 text-[10px] font-bold font-sans tracking-widest uppercase translate-y-12 group-hover:translate-y-0 transition-transform duration-300">
                  Quick Add +
                </button>
              </div>
              <h4 className="font-sans text-sm font-medium">{product.name}</h4>
              <p className="font-sans text-xs text-gray-500 mt-1">
                {product.price}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Street Snap Concept Section */}
      <section className="py-24 bg-[#1a1a1a] text-white">
        <div className="px-6 md:px-20 flex flex-col md:flex-row gap-12 items-center">
          <div className="flex-1 space-y-8">
            <h2 className="text-5xl md:text-7xl font-black uppercase leading-none italic">
              Real
              <br />
              Snap
            </h2>
            <p className="text-gray-400 font-sans leading-relaxed max-w-sm">
              We are style ing <br />
              당신의 리뷰가 화보가 됩니다. <br />
              DM으로 혜택을 확인하세요! (추첨)
              <br />
              <br />
              #Weareing #Weareing today #WhatAreYouWeareing
            </p>
            <button className="flex items-center gap-4 text-xs font-bold font-sans tracking-[0.2em] border border-white/30 px-8 py-4 hover:bg-white hover:text-black transition-all uppercase">
              <Instagram size={16} /> Share Your Look
            </button>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div className="space-y-4 pt-12">
              <img
                src="https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&q=80&w=400"
                className="w-full h-64 object-cover"
                alt="Street 1"
              />
              <img
                src="https://static.cdn.kmong.com/gigs/X8VmN1763189466.png?w=715"
                className="w-full h-80 object-cover"
                alt="Street 2"
              />
            </div>
            <div className="space-y-4">
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=400"
                className="w-full h-80 object-cover"
                alt="Street 3"
              />
              <img
                src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&q=80&w=400"
                className="w-full h-64 object-cover"
                alt="Street 4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-6 md:px-20 border-t border-black/5 bg-[#F9F8F6]">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="col-span-2">
            <h1 className="text-4xl font-black italic tracking-tighter mb-6 uppercase">
              Weareing
            </h1>
            <p className="max-w-xs text-sm font-sans text-gray-500 leading-relaxed mb-8">
              <span>문화와 세대를 연결하는 따뜻한 아카이브</span>
              <br />
              <span>위어링은 여성을 위한 따뜻한 공간이자, </span>
              <br />
              <span>일상이 되고자합니다.</span>
            </p>
            <div className="flex space-x-4">
              <Instagram size={20} className="cursor-pointer" />
              <div className="w-5 h-5 bg-black rounded-full cursor-pointer flex items-center justify-center text-[8px] text-white font-bold">
                P
              </div>
              <div className="w-5 h-5 bg-black rounded-full cursor-pointer flex items-center justify-center text-[8px] text-white font-bold">
                X
              </div>
            </div>
          </div>
          <div>
            <h5 className="font-sans font-bold text-xs tracking-widest uppercase mb-6">
              Collections
            </h5>
            <ul className="space-y-3 text-sm font-sans text-gray-500">
              <li>
                <a href="#" className="hover:text-black">
                  New Arrivals
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-black">
                  Best Sellers
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-black">
                  Editorial Pick
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-black">
                  Archive
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h5 className="font-sans font-bold text-xs tracking-widest uppercase mb-6">
              Connect
            </h5>
            <ul className="space-y-3 text-sm font-sans text-gray-500">
              <li>
                <a href="#" className="hover:text-black">
                  About Us
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-black">
                  Magazine Issue
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-black">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-black">
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-black/5 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-[10px] font-sans tracking-widest text-gray-400 uppercase">
          <span>© 2024 Magazine Co. All rights reserved.</span>
          <div className="flex space-x-8">
            <span>Shipping: Global</span>
            <span>Currency: KRW (₩)</span>
          </div>
        </div>
      </footer>

      {/* Custom Styles for Marquee Animation */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: 200%;
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default App;
